import React, { useState, useEffect } from 'react'
import {
  Heading,
  Text,
  Loader,
  AlertBanner,
  AlertBannerText,
  Flex,
  Box,
} from '@vibe/core'
import { apiService } from '../../services/api'
import { getErrorMessage } from '../../utils/errorHandling'
import type {
  SolicitudDetailResponse,
  RequirementFieldConfig,
} from '../../types'

interface JobInformationProps {
  solicitudId: string
}

/**
 * Job Information component that displays detailed job posting information
 * Similar to the Angular "Información de la Vacante" tab
 */
export const JobInformation: React.FC<JobInformationProps> = ({
  solicitudId,
}) => {
  const [solicitud, setSolicitud] = useState<
    SolicitudDetailResponse['data'] | null
  >(null)
  const [requirementFields, setRequirementFields] = useState<
    RequirementFieldConfig[]
  >([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!solicitudId) {
      setError('ID de solicitud no proporcionado')
      setLoading(false)
      return
    }

    loadJobInformation()
  }, [solicitudId])

  const loadJobInformation = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load solicitud data
      const response = await apiService.getSolicitud(solicitudId)
      setSolicitud(response.data)

      // Load requirement field configurations if this is a requirement-based solicitud
      if (response.data.requerimiento) {
        try {
          const fieldsResponse = await apiService.getRequirementFields()
          setRequirementFields(fieldsResponse.data.fields)
        } catch (fieldsError) {
          console.warn('Could not load requirement fields:', fieldsError)
          // Don't fail the whole component if fields can't be loaded
        }
      }
    } catch (err) {
      console.error('Error loading job information:', err)
      setError(
        getErrorMessage(err) || 'Error al cargar la información de la vacante'
      )
    } finally {
      setLoading(false)
    }
  }

  const formatNivelEstudios = (nivel: string): string => {
    const nivelMap: { [key: string]: string } = {
      primaria_secundaria: 'Primaria/Secundaria completa',
      tecnico_basico: 'Estudios Técnicos Básico',
      tecnico_especializado: 'Estudios Técnicos Especializados',
      universitario: 'Estudios Universitarios Completos',
      titulado: 'Titulado',
      diplomado: 'Diplomado / Especialización',
      maestria: 'Maestría',
      doctorado: 'Doctorado',
    }
    return nivelMap[nivel] || nivel
  }

  const formatCustomFieldValue = (
    field: RequirementFieldConfig,
    value: any
  ): string => {
    if (value === null || value === undefined) {
      return 'No especificado'
    }

    switch (field.field_type) {
      case 'checkbox':
        return value ? 'Sí' : 'No'
      case 'dropdown':
        return String(value)
      case 'textbox':
      default:
        return String(value)
    }
  }

  if (loading) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        className="min-h-64"
      >
        <Loader size="large" />
        <Text className="mt-4">Cargando información de la vacante...</Text>
      </Flex>
    )
  }

  if (error) {
    return (
      <AlertBanner backgroundColor="negative" className="mb-6">
        <AlertBannerText text={error} />
      </AlertBanner>
    )
  }

  if (!solicitud) {
    return (
      <AlertBanner backgroundColor="warning" className="mb-6">
        <AlertBannerText text="Información de la vacante no encontrada" />
      </AlertBanner>
    )
  }

  const jd = solicitud.jd

  // Get job data with job definition priority
  const getJobData = () => {
    if (solicitud.job_definition) {
      return {
        source: 'job_definition' as const,
        data: solicitud.job_definition,
        puesto: solicitud.job_definition.puesto,
        name: solicitud.job_definition.name,
      }
    }
    if (jd) {
      return {
        source: 'legacy' as const,
        data: jd,
        puesto: jd.estructura?.puesto_solicitado?.puesto || 'No especificado',
        name: null,
      }
    }
    return null
  }

  const jobData = getJobData()

  return (
    <div className="space-y-6">
      <Heading type="h2">Información de la Vacante</Heading>

      {/* Job Definition Information */}
      {solicitud.job_definition_name && (
        <Box className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <Heading type="h3" className="mb-4 text-blue-900">
            Definición de Puesto
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Text className="font-medium text-blue-700 mb-1">
                Nombre de la definición
              </Text>
              <Text className="text-blue-900 text-lg">
                {solicitud.job_definition_name}
              </Text>
            </div>
            <div>
              <Text className="font-medium text-blue-700 mb-1">Puesto</Text>
              <Text className="text-blue-900 text-lg">
                {solicitud.job_definition_puesto || 'No especificado'}
              </Text>
            </div>
          </div>
        </Box>
      )}

      {/* Custom Requirement Fields */}
      {solicitud.requerimiento?.custom_fields &&
        Object.keys(solicitud.requerimiento.custom_fields).length > 0 &&
        requirementFields.length > 0 && (
          <Box className="bg-white p-6 rounded-lg shadow-sm border">
            <Heading type="h3" className="mb-4">
              Información del requerimiento
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requirementFields
                .filter(
                  field =>
                    solicitud.requerimiento?.custom_fields?.[field.field_id] !==
                      undefined &&
                    solicitud.requerimiento?.custom_fields?.[field.field_id] !==
                      null &&
                    solicitud.requerimiento?.custom_fields?.[field.field_id] !==
                      ''
                )
                .map(field => (
                  <div key={field.field_id} className="mb-4">
                    <Text className="font-medium text-gray-700 mb-1">
                      {field.field_name}
                      {field.is_required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </Text>
                    <Text className="text-gray-900">
                      {formatCustomFieldValue(
                        field,
                        solicitud.requerimiento?.custom_fields?.[field.field_id]
                      )}
                    </Text>
                  </div>
                ))}
            </div>
          </Box>
        )}

      {/* Funciones del Puesto */}
      {jobData?.data?.funciones && jobData.data.funciones.length > 0 && (
        <Box className="bg-white p-6 rounded-lg shadow-sm border">
          <Heading type="h3" className="mb-4">
            Funciones del Puesto
          </Heading>
          <div className="space-y-3">
            {jobData.data.funciones.map((funcion, index) => (
              <div key={index} className="flex justify-between items-start">
                <Text className="flex-1 mr-4">{funcion.funcion}</Text>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                  {funcion.ocupabilidad}%
                </span>
              </div>
            ))}
          </div>
          {jobData.source === 'job_definition' && (
            <Text className="text-sm text-blue-600 mt-3">
              Funciones definidas en la plantilla de puesto
            </Text>
          )}
        </Box>
      )}

      {/* Requisitos Indispensables */}
      {jobData?.data?.requisitos?.indispensables &&
        jobData.data.requisitos.indispensables.length > 0 && (
          <Box className="bg-white p-6 rounded-lg shadow-sm border">
            <Heading type="h3" className="mb-4">
              Requisitos Indispensables
            </Heading>
            <ul className="space-y-2">
              {jobData.data.requisitos.indispensables.map(
                (requisito, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <Text>{requisito}</Text>
                  </li>
                )
              )}
            </ul>
            {jobData.source === 'job_definition' && (
              <Text className="text-sm text-blue-600 mt-3">
                Requisitos definidos en la plantilla de puesto
              </Text>
            )}
          </Box>
        )}

      {/* Perfil del Candidato */}
      {jobData?.data?.perfil && (
        <Box className="bg-white p-6 rounded-lg shadow-sm border">
          <Heading type="h3" className="mb-4">
            Perfil del Candidato
          </Heading>

          {/* Estudios */}
          {jobData.data.perfil.estudios &&
            jobData.data.perfil.estudios.length > 0 && (
              <div className="mb-6">
                <Text className="font-medium text-gray-700 mb-3">
                  Estudios Requeridos
                </Text>
                <div className="space-y-3">
                  {jobData.data.perfil.estudios.map((estudio, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <Text className="font-medium text-gray-900 mb-1">
                        {formatNivelEstudios(estudio.nivel)}
                      </Text>
                      <Text className="text-gray-700">{estudio.detalle}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Idiomas */}
          {jobData.data.perfil.idiomas &&
            jobData.data.perfil.idiomas.length > 0 && (
              <div className="mb-6">
                <Text className="font-medium text-gray-700 mb-3">
                  Idiomas Requeridos
                </Text>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {jobData.data.perfil.idiomas.map((idioma, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <Text className="font-medium text-gray-900">
                        {idioma.lenguaje}
                      </Text>
                      <Text className="text-gray-700">{idioma.nivel}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Conocimientos */}
          {jobData.data.perfil.conocimientos &&
            jobData.data.perfil.conocimientos.length > 0 && (
              <div>
                <Text className="font-medium text-gray-700 mb-3">
                  Conocimientos Requeridos
                </Text>
                <div className="flex flex-wrap gap-2">
                  {jobData.data.perfil.conocimientos.map(
                    (conocimiento, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {conocimiento}
                      </span>
                    )
                  )}
                </div>
              </div>
            )}

          {jobData.source === 'job_definition' && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Text className="text-sm text-blue-600">
                Perfil definido en la plantilla de puesto para garantizar
                evaluaciones consistentes
              </Text>
            </div>
          )}
        </Box>
      )}
    </div>
  )
}

export default JobInformation
